import React, { forwardRef, useState } from 'react';
import { CustomModal } from '@components';
import {
    Box,
    Checkbox,
    FormControl,
    FormControlLabel,
    InputLabel,
    ListItemText,
    MenuItem,
    OutlinedInput,
    Select,
    Switch,
    Tab,
    Tabs,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import DoneIcon from '@mui/icons-material/Done';
import { useDispatch, useSelector } from 'react-redux';
import { showToast } from '@utils';
import { apiGetProfile, apiUpdateUser, apiGetUsersForAccount } from '@store';
import { available_contract_options, available_product_options } from '@data';
import { useParams } from 'react-router-dom';
import {
    apiGetChf,
    apiGetPegnordChf,
    apiGetPegnordEur,
    apiGetTheEur,
    apiGetTtfChf,
    apiGetTtfEur
} from '../../../../../store';

const emailTooltip = `General email where trade confirmations will be sent along with the user who did that trade.`;
const purchaseLimitTooltip = `Soft purchase limit per day in MW.`;
const sellLimitTooltip = `Soft sell limit per day in MW.`;

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250
        }
    }
};

const EditUsers = forwardRef(({ selectedUser, handleClose }, ref) => {
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.user);
    const { account_uuid } = useParams();

    const [tabValue, setTabValue] = useState(0);
    const handleTabChange = (_, newValue) => setTabValue(newValue);

    const [state, setState] = useState({
        fullname: selectedUser?.fullname || '',
        email: selectedUser?.email || '',
        phone: selectedUser?.phone || '',
        visiblecontracts:
            selectedUser?.visiblecontracts?.map((contract) => contract.name) ||
            [],
        visibleproducts:
            selectedUser?.visibleproducts?.map((product) => product.name) || [],
        hide_buy_button: selectedUser?.hide_buy_button || false,
        hide_sell_button: selectedUser?.hide_sell_button || false,
        hide_trailing_stop: selectedUser?.hide_trailing_stop || false,
        disabled: selectedUser?.disabled || false,
        buyorderlimit: selectedUser?.buyorderlimit || 0,
        sellorderlimit: selectedUser?.sellorderlimit || 0,
        hideflex: selectedUser?.hideflex || false,
        username: selectedUser?.username || '',
        temporary: selectedUser?.temporary || 0,
        valid_for: selectedUser?.valid_for || 0,
        hide_portfolio: selectedUser?.hide_portfolio || false,
        hide_intraday: selectedUser?.hide_intraday || false,
        starttime: selectedUser?.starttime || '',
        endtime: selectedUser?.endtime || '',
        lunchstart: selectedUser?.lunchstart || '',
        lunchend: selectedUser?.lunchend || '',
        orderlimit: selectedUser?.orderlimit || 0,
        deal_order_limit_eur: selectedUser?.deal_order_limit_eur || 0,
        deal_order_limit_chf: selectedUser?.deal_order_limit_chf || 0,
        deal_order_limit_mw: selectedUser?.deal_order_limit_mw || 0,
        daily_buy_limit_mw: selectedUser?.daily_buy_limit_mw || 0,
        daily_sell_limit_mw: selectedUser?.daily_sell_limit_mw || 0,
        daily_buy_limit_eur: selectedUser?.daily_buy_limit_eur || 0,
        daily_buy_limit_chf: selectedUser?.daily_buy_limit_chf || 0,
        daily_sell_limit_eur: selectedUser?.daily_sell_limit_eur || 0,
        daily_sell_limit_chf: selectedUser?.daily_sell_limit_chf || 0,
        net_buy_sell_limit_mw: selectedUser?.net_buy_sell_limit_mw || 0,
        net_buy_sell_limit_chf: selectedUser?.net_buy_sell_limit_chf || 0,
        net_buy_sell_limit_eur: selectedUser?.net_buy_sell_limit_eur || 0
    });

    // for visible contracts
    const handleChangeMultipleContract = (event) => {
        const {
            target: { value }
        } = event;
        setState({
            ...state,
            visiblecontracts:
                typeof value === 'string' ? value.split(',') : value
        });
    };

    // for visible products
    const handleChangeMultipleProduct = (event) => {
        const {
            target: { value }
        } = event;
        setState({
            ...state,
            visibleproducts:
                typeof value === 'string' ? value.split(',') : value
        });
    };

    // for checkboxes
    const handleChangeCheckbox = (event) => {
        setState({ ...state, [event.target.name]: event.target.checked });
    };

    // handle toggle change
    const handleToggleChange = (e) => {
        const { name, checked } = e.target;
        setState({
            ...state,
            [name]: checked
        });
    };

    // handle change
    const handleChange = (e) => {
        const { name, value } = e.target;
        setState((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const data = {
            ...state,
            visiblecontracts: state.visiblecontracts.map((contract) => ({
                id: available_contract_options.find((c) => c.name === contract)
                    .id,
                name: contract
            })),
            visibleproducts: state.visibleproducts.map((product) => ({
                id: available_product_options.find((p) => p.name === product)
                    .id,
                name: product
            })),
            buyorderlimit: parseInt(state.buyorderlimit),
            sellorderlimit: parseInt(state.sellorderlimit),
            orderlimit: parseFloat(state.orderlimit),
            starttime: state.starttime ? state.starttime + ':00' : '',
            endtime: state.endtime,
            lunchstart: state.lunchstart ? state.lunchstart + ':00' : '',
            lunchend: state.lunchend,
            deal_order_limit_eur: parseFloat(state.deal_order_limit_eur),
            deal_order_limit_chf: parseFloat(state.deal_order_limit_chf),
            deal_order_limit_mw: parseFloat(state.deal_order_limit_mw),
            daily_buy_limit_mw: parseFloat(state.daily_buy_limit_mw),
            daily_sell_limit_mw: parseFloat(state.daily_sell_limit_mw),
            daily_buy_limit_eur: parseFloat(state.daily_buy_limit_eur),
            daily_sell_limit_eur: parseFloat(state.daily_sell_limit_eur),
            daily_buy_limit_chf: parseFloat(state.daily_buy_limit_chf),
            daily_sell_limit_chf: parseFloat(state.daily_sell_limit_chf),
            net_buy_sell_limit_eur: parseFloat(state.net_buy_sell_limit_eur),
            net_buy_sell_limit_chf: parseFloat(state.net_buy_sell_limit_chf),
            net_buy_sell_limit_mw: parseFloat(state.net_buy_sell_limit_mw)
        };

        const resp = await dispatch(apiUpdateUser(data));
        if (resp.meta.requestStatus === 'fulfilled') {
            if (user?.UUID === selectedUser?.uuid) {
                await dispatch(apiGetProfile());
            }

            dispatch(apiGetUsersForAccount(account_uuid));
            if (user?.UUID === selectedUser?.uuid) {
                if (state.visibleproducts.includes('THE')) {
                    dispatch(apiGetTheEur());
                }
                if (state.visibleproducts.includes('THE CHF')) {
                    dispatch(apiGetChf());
                }
                if (state.visibleproducts.includes('TTF')) {
                    dispatch(apiGetTtfEur());
                }
                if (state.visibleproducts.includes('TTF CHF')) {
                    dispatch(apiGetTtfChf());
                }
                if (state.visibleproducts.includes('PEG')) {
                    dispatch(apiGetPegnordEur());
                }
                if (state.visibleproducts.includes('PEG CHF')) {
                    dispatch(apiGetPegnordChf());
                }
            }
            handleClose();
            showToast('success', 'User updated successfully');
        } else if (resp.meta.requestStatus === 'rejected') {
            showToast('error', resp?.payload || 'An error occurred');
        }
    };

    return (
        <CustomModal
            handleClose={handleClose}
            handleSubmit={handleSubmit}
            modalTitle={`Edit User`}
            buttonText={'Submit'}
            ref={ref}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '1rem'
                }}
            >
                {/* Header row: enable/disable + user info */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center'
                        }}
                    >
                        <Typography sx={{ fontWeight: 'bold' }}>
                            Enable/Disable:
                        </Typography>
                        <Switch
                            name="disabled"
                            checked={state.disabled}
                            onChange={handleToggleChange}
                            inputProps={{ 'aria-label': 'controlled' }}
                        />
                    </Box>
                    <Box>
                        <Typography>{selectedUser?.username}</Typography>
                        <Typography>{selectedUser?.AccountName}</Typography>
                    </Box>
                </Box>

                {/* Tabs */}
                <Tabs
                    value={tabValue}
                    onChange={handleTabChange}
                    sx={{
                        borderBottom: 1,
                        borderColor: 'divider',
                        minHeight: 36
                    }}
                >
                    <Tab label="General" sx={{ minHeight: 36, py: 0 }} />
                    <Tab label="User Limits" sx={{ minHeight: 36, py: 0 }} />
                </Tabs>

                {/* Tab 1: General (current fields) */}
                {tabValue === 0 && (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '1rem'
                        }}
                    >
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 1fr',
                                gap: '1rem'
                            }}
                        >
                            <FormControl fullWidth={true} variant="outlined">
                                <InputLabel id="nameLabel" shrink>
                                    Name
                                </InputLabel>
                                <OutlinedInput
                                    id="nameLabel"
                                    label="Name"
                                    name="fullname"
                                    onChange={handleChange}
                                    value={state.fullname}
                                />
                            </FormControl>
                            <Tooltip title={emailTooltip} arrow>
                                <FormControl
                                    fullWidth={true}
                                    variant="outlined"
                                >
                                    <InputLabel id="emailLabel" shrink>
                                        Email
                                    </InputLabel>
                                    <OutlinedInput
                                        id="email"
                                        label="emailLabel"
                                        name="email"
                                        onChange={handleChange}
                                        value={state.email}
                                    />
                                </FormControl>
                            </Tooltip>
                            <FormControl fullWidth={true} variant="outlined">
                                <InputLabel
                                    id="phoneLabel"
                                    sx={{ bgcolor: '#F5F5F5', px: '4px' }}
                                    shrink
                                >
                                    Phone Number
                                </InputLabel>
                                <OutlinedInput
                                    id="phoneLabel"
                                    label="Phone Number"
                                    name="phone"
                                    onChange={handleChange}
                                    value={state.phone}
                                />
                            </FormControl>
                            <FormControl>
                                <InputLabel id="demo-multiple-checkbox-label">
                                    Visible Contacts
                                </InputLabel>
                                <Select
                                    labelId="demo-multiple-checkbox-label"
                                    id="demo-multiple-checkbox"
                                    multiple
                                    value={state.visiblecontracts}
                                    onChange={handleChangeMultipleContract}
                                    input={
                                        <OutlinedInput label="Visible Contacts" />
                                    }
                                    renderValue={(selected) =>
                                        selected.join(', ')
                                    }
                                    MenuProps={MenuProps}
                                >
                                    {available_contract_options?.map(
                                        (contract, index) => (
                                            <MenuItem
                                                key={index}
                                                value={contract.name}
                                            >
                                                <Checkbox
                                                    checked={
                                                        state.visiblecontracts.indexOf(
                                                            contract.name
                                                        ) > -1
                                                    }
                                                />
                                                <ListItemText
                                                    primary={contract.name}
                                                />
                                            </MenuItem>
                                        )
                                    )}
                                </Select>
                            </FormControl>
                            {/* <Box sx={{ display: 'flex', gap: 1 }}>
                                <Tooltip title={purchaseLimitTooltip} arrow>
                                    <FormControl
                                        fullWidth={true}
                                        variant="outlined"
                                    >
                                        <InputLabel
                                            id="buyOrderLimitLabel"
                                            sx={{
                                                bgcolor: '#F5F5F5',
                                                p: '0 3px'
                                            }}
                                        >
                                            Daily Buy Limit (MW)
                                        </InputLabel>
                                        <OutlinedInput
                                            id="buyOrderLimitLabel"
                                            label="Daily Buy Limit (MW)"
                                            name="buyorderlimit"
                                            type="number"
                                            inputProps={{ step: 10 }}
                                            onChange={handleChange}
                                            value={state.buyorderlimit}
                                        />
                                    </FormControl>
                                </Tooltip>
                                <Tooltip title={sellLimitTooltip} arrow>
                                    <FormControl
                                        fullWidth={true}
                                        variant="outlined"
                                    >
                                        <InputLabel
                                            id="sellOrderLimitLabel"
                                            sx={{
                                                bgcolor: '#F5F5F5',
                                                p: '0 3px'
                                            }}
                                        >
                                            Daily Sell Limit (MW)
                                        </InputLabel>
                                        <OutlinedInput
                                            id="sellOrderLimitLabel"
                                            label="Daily Sell Limit"
                                            name="sellorderlimit"
                                            type="number"
                                            inputProps={{ step: 10 }}
                                            onChange={handleChange}
                                            value={state.sellorderlimit}
                                        />
                                    </FormControl>
                                </Tooltip>
                            </Box> */}
                            <FormControl>
                                <InputLabel id="demo-multiple-checkbox-label">
                                    Visible Products
                                </InputLabel>
                                <Select
                                    labelId="demo-multiple-checkbox-label"
                                    id="demo-multiple-checkbox"
                                    multiple
                                    value={state.visibleproducts}
                                    onChange={handleChangeMultipleProduct}
                                    input={
                                        <OutlinedInput label="Visible Products" />
                                    }
                                    renderValue={(selected) =>
                                        selected.join(', ')
                                    }
                                    MenuProps={MenuProps}
                                >
                                    {available_product_options?.map(
                                        (product, index) => (
                                            <MenuItem
                                                key={index}
                                                value={product.name}
                                            >
                                                <Checkbox
                                                    checked={
                                                        state.visibleproducts.indexOf(
                                                            product.name
                                                        ) > -1
                                                    }
                                                />
                                                <ListItemText
                                                    primary={product.name}
                                                />
                                            </MenuItem>
                                        )
                                    )}
                                </Select>
                            </FormControl>
                        </Box>
                        <Box>
                            <Typography sx={{ fontWeight: 'bold' }}>
                                Please specify you want to disable
                            </Typography>
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between'
                                }}
                            >
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={state.hide_buy_button}
                                            onChange={handleChangeCheckbox}
                                            name="hide_buy_button"
                                        />
                                    }
                                    label="Disable Buy"
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={state.hide_sell_button}
                                            onChange={handleChangeCheckbox}
                                            name="hide_sell_button"
                                        />
                                    }
                                    label="Disable Sell"
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={state.hide_trailing_stop}
                                            onChange={handleChangeCheckbox}
                                            name="hide_trailing_stop"
                                        />
                                    }
                                    label="Disable TrailingStop"
                                />
                            </Box>
                        </Box>
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between'
                            }}
                        >
                            <Box>
                                <Typography sx={{ fontWeight: 'bold' }}>
                                    Visible Products
                                </Typography>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column'
                                    }}
                                >
                                    {selectedUser?.visibleproducts?.map(
                                        (product, index) => (
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    gap: '2px'
                                                }}
                                                key={index}
                                            >
                                                <DoneIcon fontSize="small" />
                                                <Typography>
                                                    {product?.name}{' '}
                                                </Typography>
                                            </Box>
                                        )
                                    )}
                                </Box>
                            </Box>
                            <Box>
                                <Typography sx={{ fontWeight: 'bold' }}>
                                    Visible Contracts
                                </Typography>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column'
                                    }}
                                >
                                    {selectedUser?.visiblecontracts?.map(
                                        (contract, index) => (
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    gap: '2px'
                                                }}
                                                key={index}
                                            >
                                                <DoneIcon fontSize="small" />
                                                <Typography>
                                                    {contract?.name}
                                                </Typography>
                                            </Box>
                                        )
                                    )}
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                )}

                {/* Tab 2: User Limits */}
                {tabValue === 1 && (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '1rem'
                        }}
                    >
                        {/* Hide Settings */}
                        {/* <Box>
                            <Typography
                                sx={{ fontWeight: 'bold', fontSize: '0.85rem' }}
                            >
                                Visibility
                            </Typography>
                            <Box sx={{ display: 'flex', gap: '1rem' }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={state.hide_portfolio}
                                            onChange={handleChangeCheckbox}
                                            name="hide_portfolio"
                                        />
                                    }
                                    label="Hide Portfolio"
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={state.hide_intraday}
                                            onChange={handleChangeCheckbox}
                                            name="hide_intraday"
                                        />
                                    }
                                    label="Hide Intraday"
                                />
                            </Box>
                        </Box> */}

                        {/* Trading Hours */}
                        {/* <Box>
                            <Typography
                                sx={{
                                    fontWeight: 'bold',
                                    fontSize: '0.85rem',
                                    mb: 0.5
                                }}
                            >
                                Trading Hours
                            </Typography>
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 1fr',
                                    gap: '0.75rem'
                                }}
                            >
                                <FormControl fullWidth variant="outlined">
                                    <InputLabel shrink>Start Time</InputLabel>
                                    <OutlinedInput
                                        label="Start Time"
                                        name="starttime"
                                        type="time"
                                        onChange={handleChange}
                                        value={state.starttime}
                                        inputProps={{ step: 60 }}
                                    />
                                </FormControl>
                                <FormControl fullWidth variant="outlined">
                                    <InputLabel shrink>End Time</InputLabel>
                                    <OutlinedInput
                                        label="End Time"
                                        name="endtime"
                                        type="time"
                                        onChange={handleChange}
                                        value={state.endtime}
                                        inputProps={{ step: 60 }}
                                    />
                                </FormControl>
                                <FormControl fullWidth variant="outlined">
                                    <InputLabel shrink>Lunch Start</InputLabel>
                                    <OutlinedInput
                                        label="Lunch Start"
                                        name="lunchstart"
                                        type="time"
                                        onChange={handleChange}
                                        value={state.lunchstart}
                                        inputProps={{ step: 60 }}
                                    />
                                </FormControl>
                                <FormControl fullWidth variant="outlined">
                                    <InputLabel shrink>Lunch End</InputLabel>
                                    <OutlinedInput
                                        label="Lunch End"
                                        name="lunchend"
                                        type="time"
                                        onChange={handleChange}
                                        value={state.lunchend}
                                        inputProps={{ step: 60 }}
                                    />
                                </FormControl>
                            </Box>
                        </Box> */}

                        {/* Order Limit */}
                        {/* <Box>
                            <Typography
                                sx={{
                                    fontWeight: 'bold',
                                    fontSize: '0.85rem',
                                    mb: 0.5
                                }}
                            >
                                Order Limit
                            </Typography>
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 1fr',
                                    gap: '0.75rem'
                                }}
                            >
                                <FormControl fullWidth variant="outlined">
                                    <InputLabel shrink>Order Limit</InputLabel>
                                    <OutlinedInput
                                        label="Order Limit"
                                        name="orderlimit"
                                        type="number"
                                        inputProps={{ step: 0.01 }}
                                        onChange={handleChange}
                                        value={state.orderlimit}
                                    />
                                </FormControl>
                            </Box>
                        </Box> */}

                        {/* Deal Limits */}
                        <Box>
                            <Typography
                                sx={{
                                    fontWeight: 'bold',
                                    fontSize: '0.85rem',
                                    mb: 0.5
                                }}
                            >
                                Deal Limits
                            </Typography>
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 1fr 1fr',
                                    gap: '0.75rem'
                                }}
                            >
                                <FormControl fullWidth variant="outlined">
                                    <InputLabel
                                        // shrink
                                        id="dealLimitEurLabel"
                                        sx={{
                                            bgcolor: '#F5F5F5',
                                            p: '0 3px'
                                        }}
                                    >
                                        Deal Limit in EUR
                                    </InputLabel>
                                    <OutlinedInput
                                        id="dealLimitEurLabel"
                                        label="Deal Limit in EUR"
                                        name="deal_order_limit_eur"
                                        type="number"
                                        inputProps={{ min: 0, step: 0.01 }}
                                        onChange={handleChange}
                                        value={state.deal_order_limit_eur}
                                    />
                                </FormControl>
                                <FormControl fullWidth variant="outlined">
                                    <InputLabel
                                        // shrink
                                        id="dealLimitChfLabel"
                                        sx={{
                                            bgcolor: '#F5F5F5',
                                            p: '0 3px'
                                        }}
                                    >
                                        Deal Limit in CHF
                                    </InputLabel>
                                    <OutlinedInput
                                        id="dealLimitChfLabel"
                                        label="Deal Limit in CHF"
                                        name="deal_order_limit_chf"
                                        type="number"
                                        inputProps={{ min: 0, step: 0.01 }}
                                        onChange={handleChange}
                                        value={state.deal_order_limit_chf}
                                    />
                                </FormControl>
                                <FormControl fullWidth variant="outlined">
                                    <InputLabel
                                        // shrink
                                        id="dealLimitMwhLabel"
                                        sx={{
                                            bgcolor: '#F5F5F5',
                                            p: '0 3px'
                                        }}
                                    >
                                        Deal Limit in MWh/h
                                    </InputLabel>
                                    <OutlinedInput
                                        id="dealLimitMwhLabel"
                                        label="Deal Limit in MWh/h"
                                        name="deal_order_limit_mw"
                                        type="number"
                                        inputProps={{ min: 0, step: 0.01 }}
                                        onChange={handleChange}
                                        value={state.deal_order_limit_mw}
                                    />
                                </FormControl>
                            </Box>
                        </Box>

                        {/* Daily Buy Limits */}
                        <Box>
                            <Typography
                                sx={{
                                    fontWeight: 'bold',
                                    fontSize: '0.85rem',
                                    mb: 0.5
                                }}
                            >
                                Daily Buy Limits
                            </Typography>
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 1fr 1fr',
                                    gap: '0.75rem'
                                }}
                            >
                                <FormControl fullWidth variant="outlined">
                                    <InputLabel
                                        // shrink
                                        id="dealLimitEurLabel"
                                        sx={{
                                            bgcolor: '#F5F5F5',
                                            p: '0 3px'
                                        }}
                                    >
                                        Daily Buy Limit in EUR
                                    </InputLabel>
                                    <OutlinedInput
                                        id="dealLimitEurLabel"
                                        label="Daily Buy Limit in EUR"
                                        name="daily_buy_limit_eur"
                                        type="number"
                                        inputProps={{ min: 0, step: 0.01 }}
                                        onChange={handleChange}
                                        value={state.daily_buy_limit_eur}
                                    />
                                </FormControl>
                                <FormControl fullWidth variant="outlined">
                                    <InputLabel
                                        // shrink
                                        id="dealLimitChfLabel"
                                        sx={{
                                            bgcolor: '#F5F5F5',
                                            p: '0 3px'
                                        }}
                                    >
                                        Daily Buy Limit in CHF
                                    </InputLabel>
                                    <OutlinedInput
                                        id="dealLimitChfLabel"
                                        label="Daily Buy Limit in CHF"
                                        name="daily_buy_limit_chf"
                                        type="number"
                                        inputProps={{ min: 0, step: 0.01 }}
                                        onChange={handleChange}
                                        value={state.daily_buy_limit_chf}
                                    />
                                </FormControl>
                                <FormControl fullWidth variant="outlined">
                                    <InputLabel
                                        // shrink
                                        id="dealLimitMwhLabel"
                                        sx={{
                                            bgcolor: '#F5F5F5',
                                            p: '0 3px'
                                        }}
                                    >
                                        Daily Buy Limit in MWh/h
                                    </InputLabel>
                                    <OutlinedInput
                                        id="dealLimitMwhLabel"
                                        label="Daily Buy Limit in MWh/h"
                                        name="daily_buy_limit_mw"
                                        type="number"
                                        inputProps={{ min: 0, step: 'any' }}
                                        onChange={handleChange}
                                        value={state.daily_buy_limit_mw}
                                    />
                                </FormControl>
                            </Box>
                        </Box>

                        {/* Daily Sell Limits */}
                        <Box>
                            <Typography
                                sx={{
                                    fontWeight: 'bold',
                                    fontSize: '0.85rem',
                                    mb: 0.5
                                }}
                            >
                                Daily Sell Limits
                            </Typography>
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 1fr 1fr',
                                    gap: '0.75rem'
                                }}
                            >
                                <FormControl fullWidth variant="outlined">
                                    <InputLabel
                                        shrink
                                        id="dealLimitEurLabel"
                                        sx={{
                                            bgcolor: '#F5F5F5',
                                            p: '0 3px'
                                        }}
                                    >
                                        Daily Sell Limit in EUR
                                    </InputLabel>
                                    <OutlinedInput
                                        id="dealLimitEurLabel"
                                        label="Daily Sell Limit in EUR"
                                        name="daily_sell_limit_eur"
                                        type="number"
                                        inputProps={{ min: 0, step: 0.01 }}
                                        onChange={handleChange}
                                        value={state.daily_sell_limit_eur}
                                    />
                                </FormControl>
                                <FormControl fullWidth variant="outlined">
                                    <InputLabel
                                        shrink
                                        id="dealLimitChfLabel"
                                        sx={{
                                            bgcolor: '#F5F5F5',
                                            p: '0 3px'
                                        }}
                                    >
                                        Daily Sell Limit in CHF
                                    </InputLabel>
                                    <OutlinedInput
                                        id="dealLimitChfLabel"
                                        label="Daily Sell Limit in CHF"
                                        name="daily_sell_limit_chf"
                                        type="number"
                                        inputProps={{ min: 0, step: 0.01 }}
                                        onChange={handleChange}
                                        value={state.daily_sell_limit_chf}
                                    />
                                </FormControl>
                                <FormControl fullWidth variant="outlined">
                                    <InputLabel
                                        // shrink
                                        id="dealLimitMwhLabel"
                                        sx={{
                                            bgcolor: '#F5F5F5',
                                            p: '0 3px'
                                        }}
                                    >
                                        Daily Sell Limit in MWh/h
                                    </InputLabel>
                                    <OutlinedInput
                                        id="dealLimitMwhLabel"
                                        label="Daily Sell Limit in MWh/h"
                                        name="daily_sell_limit_mw"
                                        type="number"
                                        inputProps={{ min: 0, step: 0.01 }}
                                        onChange={handleChange}
                                        value={state.daily_sell_limit_mw}
                                    />
                                </FormControl>
                            </Box>
                        </Box>

                        {/* Net Buy Sell Limits */}
                        <Box>
                            <Typography
                                sx={{
                                    fontWeight: 'bold',
                                    fontSize: '0.85rem',
                                    mb: 0.5
                                }}
                            >
                                Net Buy/Sell Limits
                            </Typography>
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 1fr 1fr',
                                    gap: '0.75rem'
                                }}
                            >
                                <FormControl fullWidth variant="outlined">
                                    <InputLabel
                                        // shrink
                                        id="dealLimitEurLabel"
                                        sx={{
                                            bgcolor: '#F5F5F5',
                                            p: '0 3px'
                                        }}
                                    >
                                        Net Buy/Sell Limit in Eur
                                    </InputLabel>
                                    <OutlinedInput
                                        id="dealLimitEurLabel"
                                        label="Net Buy Sell Limit in Eur"
                                        name="net_buy_sell_limit_eur"
                                        type="number"
                                        inputProps={{ min: 0, step: 0.01 }}
                                        onChange={handleChange}
                                        value={state.net_buy_sell_limit_eur}
                                    />
                                </FormControl>

                                <FormControl fullWidth variant="outlined">
                                    <InputLabel
                                        // shrink
                                        id="dealLimitChfLabel"
                                        sx={{
                                            bgcolor: '#F5F5F5',
                                            p: '0 3px'
                                        }}
                                    >
                                        Net Buy/Sell Limit in CHF
                                    </InputLabel>
                                    <OutlinedInput
                                        id="dealLimitChfLabel"
                                        label="Net Buy/Sell Limit in CHF"
                                        name="net_buy_sell_limit_chf"
                                        type="number"
                                        inputProps={{ min: 0, step: 0.01 }}
                                        onChange={handleChange}
                                        value={state.net_buy_sell_limit_chf}
                                    />
                                </FormControl>
                                <FormControl fullWidth variant="outlined">
                                    <InputLabel
                                        // shrink
                                        id="dealLimitMwhLabel"
                                        sx={{
                                            bgcolor: '#F5F5F5',
                                            p: '0 3px'
                                        }}
                                    >
                                        Net Buy/Sell Limit in MWh/h
                                    </InputLabel>
                                    <OutlinedInput
                                        id="dealLimitMwhLabel"
                                        label="Net Buy Sell Limit in MWh/h"
                                        name="net_buy_sell_limit_mw"
                                        type="number"
                                        inputProps={{ min: 0, step: 0.01 }}
                                        onChange={handleChange}
                                        value={state.net_buy_sell_limit_mw}
                                    />
                                </FormControl>
                            </Box>
                        </Box>
                    </Box>
                )}
            </Box>
        </CustomModal>
    );
});

export default EditUsers;
